/**
 * <ShareValidationsDialog />
 *
 * Permet à l'enseignant de partager les validations REMC posées pendant
 * la séance avec son élève, via :
 *  - un QR code scannable au smartphone
 *  - un lien copiable (collable dans un mail, SMS…)
 *
 * Côté élève, l'URL `/mon-livret-moto?import=<base64>` ouvre un dialogue
 * d'import qui appelle l'API et applique les statuts au livret personnel.
 *
 * Pas de backend additionnel nécessaire : tout est encodé dans l'URL.
 *
 * Props :
 *  - open : boolean
 *  - onOpenChange(boolean)
 *  - validations : object { 'C1-e': 'acquis', … }
 *  - studentName : string (optionnel — pour personnaliser le message)
 *  - context : 'plateau' | 'circulation' (optionnel)
 */
import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Copy, Check, Smartphone, BookOpen, QrCode, Mail } from 'lucide-react';
import { toast } from 'sonner';

const APP_ORIGIN = (typeof window !== 'undefined' && window.location?.origin) || 'https://code2roues.fr';

function encodePayload(validations, studentName, context) {
  // base64url sans padding pour des URLs courtes
  const payload = {
    v: 1, // version du format
    ts: Date.now(),
    items: validations,
    student: studentName || undefined,
    context: context || undefined,
  };
  const json = JSON.stringify(payload);
  // btoa nécessite ASCII → encoder en UTF-8 puis base64 puis URL-safe
  const utf8 = unescape(encodeURIComponent(json));
  const b64 = btoa(utf8);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export default function ShareValidationsDialog({
  open, onOpenChange, validations, studentName, context,
}) {
  const [copied, setCopied] = useState(false);

  const count = Object.keys(validations || {}).length;

  const shareUrl = useMemo(() => {
    if (!validations || count === 0) return '';
    const token = encodePayload(validations, studentName, context);
    return `${APP_ORIGIN}/mon-livret-moto?import=${token}`;
  }, [validations, studentName, context, count]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error('Impossible de copier — sélectionne manuellement le lien.');
    }
  };

  const handleMailto = () => {
    const subject = encodeURIComponent(`Tes validations REMC — séance ${context || ''}`);
    const body = encodeURIComponent(
      `Salut${studentName ? ' ' + studentName : ''} !\n\n` +
      `Voici les ${count} validation${count > 1 ? 's' : ''} REMC de notre séance.\n` +
      `Ouvre ce lien depuis ton compte code2roues.fr pour les reporter automatiquement dans ton livret :\n\n` +
      `${shareUrl}\n\n` +
      `À très vite !`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="share-validations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-[#1d4ed8]" />
            Partager au livret élève
          </DialogTitle>
          <DialogDescription>
            {count > 0
              ? `Transmets ces ${count} validation${count > 1 ? 's' : ''} REMC à l'élève. Il pourra les reporter en un clic dans son livret personnel.`
              : 'Aucune validation à partager pour le moment.'}
          </DialogDescription>
        </DialogHeader>

        {count > 0 && (
          <div className="space-y-4">
            {/* QR Code */}
            <div className="flex flex-col items-center gap-2 rounded-xl border-2 border-[#1d4ed8]/30 bg-[#1d4ed8]/5 p-4">
              <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-[#1d4ed8]">
                <QrCode className="w-3.5 h-3.5" /> Scan smartphone
              </div>
              <div className="rounded-lg bg-white p-3" data-testid="share-qr">
                <QRCodeSVG
                  value={shareUrl}
                  size={180}
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="text-[11px] text-center text-muted-foreground italic max-w-[280px]">
                <Smartphone className="inline w-3 h-3 mr-1" />
                L&apos;élève scanne avec son téléphone, se connecte si besoin,
                puis confirme l&apos;import dans son livret.
              </p>
            </div>

            {/* Lien copiable */}
            <div className="space-y-1.5">
              <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                Lien à copier-coller
              </div>
              <div className="flex items-center gap-1.5">
                <code
                  className="flex-1 rounded-lg border border-border bg-muted/40 px-2 py-1.5 text-[10px] truncate font-mono"
                  data-testid="share-url"
                  title={shareUrl}
                >
                  {shareUrl}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCopy}
                  className="gap-1 h-8 text-xs flex-shrink-0"
                  data-testid="share-copy-btn"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copié' : 'Copier'}
                </Button>
              </div>
            </div>

            {/* CTA Mail */}
            <Button
              type="button"
              onClick={handleMailto}
              variant="outline"
              className="w-full gap-1.5"
              data-testid="share-mail-btn"
            >
              <Mail className="w-4 h-4" />
              Envoyer par mail à l&apos;élève
            </Button>

            <p className="text-[10px] text-center text-muted-foreground italic">
              Aucune donnée n&apos;est envoyée à nos serveurs — tout est encodé dans le lien.
              L&apos;élève doit être connecté à son compte pour appliquer l&apos;import.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Helper d'usage côté élève ─────────────────────────────────── */

/**
 * Décode un payload depuis un paramètre URL `import`.
 * Retourne { ok: true, payload } ou { ok: false, error: 'msg' }.
 */
export function decodeImportPayload(rawToken) {
  if (!rawToken || typeof rawToken !== 'string') {
    return { ok: false, error: 'Token manquant' };
  }
  try {
    // Restaure base64 standard
    const b64 = rawToken.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + 3) % 4);
    const utf8 = atob(padded);
    const json = decodeURIComponent(escape(utf8));
    const payload = JSON.parse(json);
    if (!payload || typeof payload !== 'object' || !payload.items) {
      return { ok: false, error: 'Format invalide' };
    }
    if (payload.v !== 1) {
      return { ok: false, error: 'Version de partage non supportée' };
    }
    return { ok: true, payload };
  } catch (e) {
    return { ok: false, error: 'Lien corrompu ou expiré' };
  }
}
